/**
 * Centralized UI strings and messages
 * All user-facing text should be defined here for easy management and localization
 */

export const APP_STRINGS = {
  APP_NAME: 'MamBaby',
  APP_TAGLINE: 'AI Health Assistant',
  ASSISTANT_NAME: 'MamBaby Assistant',
  LOADING: 'Loading...',
} as const;

export const AUTH_STRINGS = {
  // Login page
  LOGIN: {
    TITLE: "Let's Sign You In",
    SUBTITLE: 'Welcome Back! 👋 Please Enter Your Details',
    EMAIL_LABEL: 'Email Address',
    EMAIL_PLACEHOLDER: 'Enter your email address',
    PASSWORD_LABEL: 'Password',
    PASSWORD_PLACEHOLDER: 'Enter your password',
    FORGOT_PASSWORD: 'Forgot Password?',
    SUBMIT_BUTTON: 'Login',
    LOADING_TEXT: 'Signing in...',
    NO_ACCOUNT: "Don't have an account?",
    SIGN_UP_LINK: 'Sign Up',
    ERROR_MESSAGE: 'Login failed. Please check your credentials.',
    LOCKOUT_ERROR:
      'Too many failed verification attempts. Please try again later or contact your administrator.',
    LOGIN_LOCKOUT_ERROR:
      'Your account has been temporarily locked due to too many failed login attempts. Please try again after 30 minutes or contact your administrator.',
    LOCKOUT_ALERT_DISMISS: 'Dismiss',
  },

  // Register page
  REGISTER: {
    TITLE: 'Create Account',
    SUBTITLE: 'Welcome! 👋 Please Enter Your Details',
    NAME_LABEL: 'Full Name',
    NAME_PLACEHOLDER: 'John Doe',
    EMAIL_LABEL: 'Email Address',
    EMAIL_PLACEHOLDER: 'Enter your email address',
    PASSWORD_LABEL: 'Password',
    PASSWORD_PLACEHOLDER: 'Create a strong password',
    CONFIRM_PASSWORD_LABEL: 'Confirm Password',
    CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your password',
    COUNTRY_LABEL: 'Country',
    COUNTRY_PLACEHOLDER: 'Select your country',
    STATE_LABEL: 'State',
    STATE_PLACEHOLDER: 'Select your state',
    SUBMIT_BUTTON: 'Sign Up',
    LOADING_TEXT: 'Creating account...',
    HAVE_ACCOUNT: 'Already have an account?',
    LOGIN_LINK: 'Sign In',
    ERROR_MESSAGE: 'Registration failed. Please try again.',
    SUCCESS_TITLE: 'Check Your Email',
    SUCCESS_MESSAGE:
      "We've sent you an email with a link to set your password. Please check your email and click the link to complete your account setup.",
    GO_TO_LOGIN: 'Back to Login',
    CONSENT_LABEL_PREFIX: 'I agree to the ',
    CONSENT_LABEL_SEPARATOR: ' and ',
    CONSENT_POLICY_LINK: 'Consent Policy',
    PRIVACY_POLICY_LINK: 'Privacy Policy',
    CONSENT_REQUIRED:
      'You must accept the Consent Policy and Privacy Policy to continue.',
  },

  // Common auth strings
  COMMON: {
    CONTACT_SUPPORT: 'Contact Support',
  },
} as const;

export const DASHBOARD_STRINGS = {
  WELCOME_MESSAGE: 'Welcome,',
  DESCRIPTION: 'Your AI-powered maternal and baby health assistant',
  LOGOUT_BUTTON: 'Logout',
  LOGOUT_LOADING: 'Logging out...',
  STATUS_ONLINE: 'Online',
  TABS: {
    VOICE_JOURNAL: 'Voice Journal',
    QA: 'Q&A',
  },
  SESSION: {
    CONFIRM_END_TITLE: 'End Session',
    CONFIRM_END_DESCRIPTION:
      'Are you sure you want to end the current session? This will clear your conversation history.',
  },
} as const;

export const VOICE_JOURNAL_STRINGS = {
  TITLE: 'Voice journal',
  SUBTITLE:
    "Tap the mic and speak freely. When you're done, we'll gently organize your thoughts.",
  STATUS: {
    IDLE: 'Tap to Record Your Journal Entry',
    PREPARING: 'Getting ready…',
    RECORDING: "Tap to stop when you're done.",
    PROCESSING: 'Processing with',
    COMPLETED: 'Recording Complete',
  },
  LISTENING: 'Listening...',
  NEW_RECORDING: 'New Recording',
  NEW_ENTRY: '+ New entry',
  COMPLETED_TITLE: 'Your journal',
  COMPLETED_SUBTITLE: 'A gentle summary of what you shared.',
  NO_THEMES:
    "We couldn't find clear themes in this entry — try sharing a bit more next time.",
  EMPTY_STATE: 'Your microphone will be used only while recording.',
  LIMITS_HINT:
    'Recording stops automatically after 30 seconds of silence or 10 minutes total.',
  JOURNAL: {
    FEELING: 'Feeling',
    MOOD: 'Mood',
    BODY_SYMPTOMS: 'Body Symptoms',
    STOMACH_PAIN: 'Stomach Pain',
    PRODUCTIVITY: 'Productivity',
    PERIOD_BLEEDING: 'Period & Bleeding',
    EXTERNAL_INFLUENCES: 'External Influences',
    NUTRITION: 'Nutrition',
    SEXUAL_HEALTH: 'Sexual Health',
    PREGNANCY_TESTING: 'Pregnancy Testing',
    MOVEMENT: 'Movement & Exercise',
    SKIN: 'Skin Condition',
    CERVICAL_MUCUS: 'Cervical Mucus',
    INTENSITY: 'Intensity',
    NO_DATA: 'Not recorded',
  },
  FALLBACK: {
    NONE_DETECTED: 'None detected',
    PROCESSING: 'Processing...',
  },
  ERRORS: {
    NO_SPEECH: 'No speech detected. Please try again.',
    TOKEN_FAILED: 'Failed to get speech token. Please check your connection.',
    MICROPHONE_FAILED:
      'Failed to start recording. Please check microphone permissions.',
    JOURNAL_FAILED: 'Failed to create journal entry. Please try again.',
  },
  TIMEOUTS: {
    SILENCE: 'Recording stopped — no speech detected for a while.',
    MAX_DURATION: 'Recording stopped after reaching the 10-minute limit.',
  },
  SUCCESS: {
    JOURNAL_CREATED: 'Journal entry created successfully!',
  },
} as const;

export const QA_STRINGS = {
  SOURCES_LABEL: 'Sources',
  INPUT_PLACEHOLDER: 'Type your question...',
  INPUT_INITIALIZING: 'Initializing...',
  EMPTY_STATE:
    'Hello, I’m an AI assistant here to help. Please ask any questions related to pregnancy, menstrual cycles, or baby care.',
  ERROR_LOAD_MESSAGES: 'Failed to load messages. Please try again.',
  TYPING_INDICATOR: 'Thinking...',
  COMPOSER_PLACEHOLDER: "Share what's on your mind\u2026",
  COMPOSER_SEND_LABEL: 'Send',
  COMPOSER_CHAR_LIMIT_TITLE: '{limit} character limit',
  COMPOSER_CHAR_COUNT_LABEL: '{count} of {limit} characters used',
  FEEDBACK: {
    POSITIVE_TITLE: 'Glad that was helpful!',
    NEGATIVE_TITLE: 'Thanks for letting us know.',
    DESCRIPTION: 'Tell us more. Your note helps us improve.',
    PLACEHOLDER: 'What worked, what didn\u2019t\u2026',
    SUBMIT_BUTTON: 'Submit',
    SKIP_BUTTON: 'Skip',
    CHAR_COUNT: '{count} / {limit}',
    SUCCESS: 'Thank you for your feedback!',
  },
  JOURNAL_DETECTION: {
    TITLE: 'Journal Entry Detected',
    DESCRIPTION: 'Do you want to save this as a journal entry?',
    CONFIRM_BUTTON: 'Yes',
    CANCEL_BUTTON: 'No',
  },
  REVIEW_PENDING_LABEL:
    'Your question is under medical review. We’ll notify you once the answer is ready.',
  REVIEW_PROMPT_QUESTION:
    'Your query has been flagged for medical review. Would you like to send it for human review?',
  REVIEW_PROMPT_YES: 'Yes',
  REVIEW_PROMPT_NO: 'No',
  REVIEW_DECLINED_LABEL:
    'We’re unable to answer your query at the moment. Please feel free to ask any other questions you may have.',
  HUMAN_APPROVED_BADGE: 'Human Reviewed',
  AI_GENERATED_BADGE: 'AI Generated',
  PROFESSIONAL_REVIEW_PROMPT_QUESTION:
    'Would you like to send this response for professional review?',
  PROFESSIONAL_REVIEW_PROMPT_YES: 'Yes',
  PROFESSIONAL_REVIEW_PROMPT_NO: 'No',
  PROFESSIONAL_REVIEW_ACCEPTED_LABEL:
    'Your question is under medical review. We’ll notify you once the answer is ready.',
} as const;

export const ERROR_STRINGS = {
  TITLE: 'Oops! Something went wrong',
  DESCRIPTION:
    'We are sorry for the inconvenience. The application encountered an unexpected error.',
  ERROR_DETAILS_LABEL: 'Error Details:',
  TRY_AGAIN_BUTTON: 'Try Again',
  GO_HOME_BUTTON: 'Go to Home',
  CONTACT_SUPPORT: 'If this problem persists, please contact support.',
  DEFAULT_API_ERROR: 'Something went wrong, please try after some time.',
  NETWORK_ERROR:
    'Network connection lost. Please check your internet and try again.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
} as const;

export const TABLE_STRINGS = {
  RESULTS_LABEL: 'Results:',
  PREVIOUS_PAGE: 'Go to previous page',
  NEXT_PAGE: 'Go to next page',
  MORE_PAGES: 'More pages',
  GO_TO_PAGE: 'Go to page {page}',
} as const;

// Shared values reused across ADMIN_STRINGS namespaces — update once, applies everywhere
const _SEARCH_BY_NAME_EMAIL = 'Search by name or email';
const _CLEAR_SEARCH = 'Clear search';

export const ADMIN_STRINGS = {
  PAGE_TITLE: 'User Sessions',
  PAGE_DESCRIPTION: 'View and manage user chat sessions.',
  BREADCRUMB_ADMIN: 'Admin',
  NAV: {
    USER_SESSIONS: 'User Sessions',
    USER_MANAGEMENT: 'User Management',
    SECTION_LABEL: 'Navigation',
  },
  LAYOUT: {
    LOGO_ALT: 'MAM Logo',
    PORTAL_TITLE: 'Admin Portal',
    STATUS_ONLINE: 'Online',
    LOGOUT: 'Logout',
    LOGOUT_LOADING: 'Logging out…',
  },
  TABLE: {
    NO_COLUMN: 'No.',
    NAME_COLUMN: 'Name',
    SESSION_ID_COLUMN: 'Session ID',
    USER_NAME_COLUMN: 'User Name',
    EMAIL_COLUMN: 'Email',
    START_DATE_COLUMN: 'Start Date',
    VIEW_CHAT_ARIA_LABEL: (sessionId: string) =>
      `View chat transcript for session ${sessionId}`,
    VIEW_COLUMN: 'View',
    NO_DATA: 'No sessions found',
    LOADING: 'Loading sessions...',
    LOADING_DESCRIPTION: 'Please wait while we fetch the session data',
    EMPTY_SEARCH_HINT: 'Try adjusting your search criteria',
    SEARCH_PLACEHOLDER: _SEARCH_BY_NAME_EMAIL,
    SEARCH_ARIA_LABEL: 'Search sessions',
    CLEAR_SEARCH_ARIA_LABEL: _CLEAR_SEARCH,
  },
  DIALOG: {
    TITLE: 'Chat Transcript',
    SESSION_INFO: 'Session ID:',
    CLOSE_BUTTON: 'Close',
    NO_MESSAGES: 'No messages in this session',
    LOADING: 'Loading messages...',
    USER_LABEL: 'User',
    AI_LABEL: 'Bot',
    FEEDBACK_POSITIVE: 'The user liked the response.',
    FEEDBACK_NEGATIVE: 'The user disliked the response.',
    FEEDBACK_POSITIVE_WITH_COMMENT:
      'The user liked the response and added comments.',
    FEEDBACK_NEGATIVE_WITH_COMMENT:
      'The user disliked the response and added comments.',
    FEEDBACK_COMMENT: 'Feedback:',
    NO_FEEDBACK: 'No feedback yet',
  },
  PAGINATION: {
    PREVIOUS: 'Previous',
    NEXT: 'Next',
    PAGE_OF: 'Page {current} of {total}',
  },
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load user sessions',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while loading the data. Please refresh the page or contact support.',
  },
  USER_MANAGEMENT: {
    PAGE_TITLE: 'User Management',
    PAGE_SUBTITLE: 'Manage users and their roles.',
    SEARCH_PLACEHOLDER: _SEARCH_BY_NAME_EMAIL,
    SEARCH_ARIA_LABEL: 'Search users',
    CLEAR_SEARCH_ARIA_LABEL: _CLEAR_SEARCH,
    TOTAL_COUNT: '{count} {label} total',
    TOTAL_LABEL_SINGULAR: 'user total',
    TOTAL_LABEL_PLURAL: 'users total',
    TABLE: {
      NO_COLUMN: 'No.',
      NAME_COLUMN: 'Name',
      EMAIL_COLUMN: 'Email',
      COUNTRY_COLUMN: 'Country',
      STATE_COLUMN: 'State',
      ROLE_COLUMN: 'Role',
      DOMAIN_COLUMN: 'Domain',
      STATUS_COLUMN: 'Status',
      CREATED_AT_COLUMN: 'Created At',
      NO_DATA: 'No users found',
      LOADING: 'Loading users...',
      LOADING_DESCRIPTION: 'Please wait while we fetch the user data',
      EMPTY_SEARCH_HINT: 'Try adjusting your search criteria',
      STATUS_ACTIVE: 'Active',
      STATUS_INACTIVE: 'Inactive',
      STATUS_PENDING: 'Pending',
      STATUS_DELETED: 'Deleted',
      STATUS_SENDING: 'Sending...',
    },
    FILTER: {
      ROLE_LABEL: 'Role',
      ROLE_ALL: 'All Roles',
      STATUS_LABEL: 'Status',
      STATUS_ALL: 'All Statuses',
      ROLE_ADMIN: 'Admin',
      ROLE_REVIEWER: 'Reviewer',
      ROLE_USER: 'User',
    },
    ACTIONS: {
      MENU_LABEL: 'User actions',
      DEACTIVATE: 'Deactivate',
      REACTIVATE: 'Reactivate',
      RESEND_INVITATION: 'Resend Invitation',
      UNLOCK: 'Unlock',
      DEACTIVATE_CONFIRM_TITLE: 'Deactivate User',
      DEACTIVATE_CONFIRM_DESCRIPTION:
        'This will sign out all active sessions and prevent login. This action can be reversed.',
      DEACTIVATE_CONFIRM_BUTTON: 'Yes, deactivate',
      CANCEL: 'Cancel',
      UNLOCK_CONFIRM_TITLE: 'Unlock User',
      UNLOCK_CONFIRM_DESCRIPTION:
        'This will immediately allow the user to log in again and reset their login attempt counters.',
      UNLOCK_CONFIRM_BUTTON: 'Yes, unlock',
      DEACTIVATE_SUCCESS: 'User deactivated successfully',
      REACTIVATE_SUCCESS: 'User reactivated successfully',
      RESEND_SUCCESS: 'Invitation resent successfully',
      UNLOCK_SUCCESS: 'User unlocked successfully',
      DEACTIVATE_ERROR: 'Failed to deactivate user',
      REACTIVATE_ERROR: 'Failed to reactivate user',
      RESEND_ERROR: 'Failed to resend invitation',
      UNLOCK_ERROR: 'Failed to unlock user',
    },
    ADD_USER: {
      BUTTON: 'Add User',
      DIALOG_TITLE: 'Add New User',
      DIALOG_DESCRIPTION: 'Fill in the details to create a new user.',
      NAME_LABEL: 'Full Name',
      NAME_PLACEHOLDER: 'John Doe',
      EMAIL_LABEL: 'Email Address',
      EMAIL_PLACEHOLDER: 'john@example.com',
      ROLE_LABEL: 'Role',
      ROLE_PLACEHOLDER: 'Select a role',
      ROLE_ADMIN: 'Admin',
      ROLE_REVIEWER: 'Reviewer',
      ROLE_USER: 'User',
      COUNTRY_LABEL: 'Country',
      COUNTRY_PLACEHOLDER: 'Select a country',
      STATE_LABEL: 'State',
      STATE_PLACEHOLDER: 'Select a state',
      LOADING_PLACEHOLDER: 'Loading...',
      DOMAIN_LABEL: 'Domain',
      DOMAIN_PLACEHOLDER: 'Select domain areas',
      DOMAIN_REMOVE_ARIA: 'Remove {name}',
      SUBMIT_BUTTON: 'Create User',
      SUBMIT_LOADING: 'Creating...',
      CANCEL_BUTTON: 'Cancel',
      SUCCESS_MESSAGE: 'User created successfully.',
    },
    EDIT_USER: {
      BUTTON: 'Edit',
      DIALOG_TITLE: 'Edit User',
      DIALOG_DESCRIPTION: "Update the user's profile details.",
      NAME_LABEL: 'Full Name',
      NAME_PLACEHOLDER: 'John Doe',
      COUNTRY_LABEL: 'Country',
      COUNTRY_PLACEHOLDER: 'Select a country',
      STATE_LABEL: 'State',
      STATE_PLACEHOLDER: 'Select a state',
      LOADING_PLACEHOLDER: 'Loading...',
      DOMAIN_LABEL: 'Domain',
      DOMAIN_PLACEHOLDER: 'Select domain areas',
      DOMAIN_REMOVE_ARIA: 'Remove {name}',
      SUBMIT_BUTTON: 'Save Changes',
      SUBMIT_LOADING: 'Saving...',
      CANCEL_BUTTON: 'Cancel',
      SUCCESS_MESSAGE: 'User updated successfully.',
      ERROR_MESSAGE: 'Failed to update user. Please try again.',
    },
    ERROR: {
      LOAD_FAILED_TITLE: 'Failed to load users',
      LOAD_FAILED_DESCRIPTION:
        'An error occurred while loading users. Please refresh the page or contact support.',
      CREATE_FAILED: 'Failed to create user. Please try again.',
    },
  },
  REVIEWS_FEEDBACK: {
    PAGE_TITLE: 'Reviews Feedback',
    PAGE_DESCRIPTION: 'View reviewer feedback submitted for flagged cases.',
    NAV_LABEL: 'Reviews Feedback',
    BREADCRUMB: 'Reviews Feedback',
    TABLE: {
      NO_COLUMN: 'No.',
      QUERY_COLUMN: 'Query',
      STATUS_COLUMN: 'Status',
      RISK_COLUMN: 'Risk',
      REVIEWER_COLUMN: 'Reviewer',
      DOMAIN_COLUMN: 'Domain',
      FEEDBACK_COLUMN: 'Feedback',
      REVIEWED_AT_COLUMN: 'Reviewed At',
      NO_DATA: 'No reviews found',
      NO_DATA_DESCRIPTION: 'Try adjusting your filter criteria',
      LOADING: 'Loading reviews...',
      LOADING_DESCRIPTION: 'Please wait while we fetch the review data',
      VIEW_DETAIL: 'View Detail',
    },
    FILTER: {
      STATUS_ALL: 'All Statuses',
      FEEDBACK_SUBMITTED: 'Submitted',
      FEEDBACK_NOT_SUBMITTED: 'Not Submitted',
    },
    STATUS: {
      PENDING: 'Pending',
      IN_PROGRESS: 'In Progress',
      APPROVED: 'Approved',
      EDITED_AND_APPROVED: 'Edited & Approved',
      REJECTED: 'Rejected',
    },
    RISK: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      CRITICAL: 'Critical',
    },
    DETAIL_DIALOG: {
      TITLE: 'Review Feedback Detail',
      REVIEW_ID_LABEL: 'Case ID',
      CLOSE: 'Close',
      ORIGINAL_QUERY_LABEL: 'Original Query',
      AI_RESPONSE_LABEL: 'AI Generated Response',
      DELIVERED_RESPONSE_LABEL: 'Delivered Response',
      STATUS_LABEL: 'Status',
      RISK_LABEL: 'Risk',
      REVIEWER_LABEL: 'Reviewer',
      DOMAIN_LABEL: 'Domain',
      REVIEWED_AT_LABEL: 'Reviewed At',
      FEEDBACK_SUBMITTED_LABEL: 'Feedback Submitted',
      RATINGS_LABEL: 'Ratings',
      NOTES_LABEL: 'Additional Notes',
      SYSTEMATIC_LABEL: 'Systematic Issue',
      YES: 'Yes',
      NO: 'No',
      NOT_SUBMITTED: 'No feedback submitted for this review.',
      LOADING: 'Loading feedback details...',
    },
    ERROR: {
      LOAD_FAILED_TITLE: 'Failed to load reviews feedback',
      LOAD_FAILED_DESCRIPTION:
        'An error occurred while loading the data. Please refresh the page.',
      DETAIL_LOAD_FAILED: 'Failed to load feedback details.',
    },
  },
} as const;

export const KNOWLEDGE_STRINGS = {
  NAV: {
    LABEL: 'Knowledge Management',
  },
  PAGE: {
    TITLE: 'Knowledge Management',
    SUBTITLE:
      'Upload, manage, and organise approved medical documents for the AI knowledge base.',
    BREADCRUMB: 'Knowledge Management',
  },
  TABS: {
    DOCUMENTS: 'Documents',
    LINKS: 'Links',
  },
  UPLOAD: {
    BUTTON: 'Upload Document',
    DIALOG_TITLE: 'Upload Medical Document',
    DIALOG_DESCRIPTION:
      'Upload a PDF or plain text file and fill in the required metadata.',
    DROPZONE_LABEL: 'Drag & drop or click to select',
    DROPZONE_HINT: 'PDF or plain text, max 50 MB',
    DROPZONE_SELECTED: 'Selected file',
    FIELDS: {
      TITLE_LABEL: 'Document Title',
      TITLE_PLACEHOLDER: 'Enter document title',
      CATEGORY_LABEL: 'Category',
      CATEGORY_PLACEHOLDER: 'Select category',
      CATEGORY_EMPTY: 'No categories available.',
      DOMAINS_LABEL: 'Domain(s)',
      DOMAINS_PLACEHOLDER: 'Select domain',
      DOMAINS_PLACEHOLDER_NO_CATEGORY: 'Select a category first',
      DOMAINS_EMPTY_FOR_CATEGORY:
        'This category has no domains yet. Pick another category.',
      DOMAINS_REMOVE_ARIA: 'Remove {name}',
      COUNTRY_LABEL: 'Country/Region',
      COUNTRY_PLACEHOLDER: 'Select your country',
      COUNTRY_LOADING_PLACEHOLDER: 'Loading countries...',
      VERSION_LABEL: 'Version',
      VERSION_PLACEHOLDER: 'e.g. 1',
    },
    SUBMIT_BUTTON: 'Upload',
    SUBMIT_LOADING: 'Uploading...',
    STEP_INITIATING: 'Preparing upload…',
    STEP_UPLOADING: 'Uploading to storage…',
    STEP_CONFIRMING: 'Finalising…',
    CANCEL_BUTTON: 'Cancel',
    SUCCESS: 'Document uploaded. Processing started.',
    ERRORS: {
      FILE_REQUIRED: 'Please select a file.',
      FILE_REJECTED:
        'File not accepted. Please upload a PDF or plain text file under 50 MB.',
      UPLOAD_FAILED: 'Upload failed. Please try again.',
    },
  },
  TABLE: {
    COLUMNS: {
      NO: 'No.',
      TITLE: 'Title',
      CATEGORY: 'Category',
      DOMAINS: 'Domain',
      COUNTRY: 'Country/Region',
      VERSION: 'Version',
      APPROVAL_DATE: 'Approval Date',
      STATUS: 'Status',
    },
    ACTIONS: {
      MENU_LABEL: 'Source actions',
      VIEW_DETAIL: 'View Detail',
      RETRY: 'Retry',
      MORE_DOMAINS_ARIA: (count: number) =>
        `Show ${count} more domain${count === 1 ? '' : 's'}`,
      MORE_CATEGORIES_ARIA: (count: number) =>
        `Show ${count} more categor${count === 1 ? 'y' : 'ies'}`,
    },
    EMPTY_TITLE: 'No knowledge sources found',
    EMPTY_DESCRIPTION: 'Upload a document to get started',
    EMPTY_SEARCH_HINT: 'Try adjusting your search or filters',
    LOADING_TITLE: 'Loading knowledge sources...',
    LOADING_DESCRIPTION: 'Please wait while we fetch the data',
  },
  STATUS: {
    PROCESSING: 'Processing',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PROCESSING_FAILED: 'Processing Failed',
  },
  LINKS_TABLE: {
    COLUMNS: {
      NO: 'No.',
      URL: 'URL',
      STATUS: 'Status',
      CREATED_AT: 'Created',
    },
    STATUS: {
      active: 'Active',
      inactive: 'Inactive',
    },
    EMPTY_TITLE: 'No links found',
    EMPTY_DESCRIPTION: 'No reference links have been added yet',
    LOADING_TITLE: 'Loading links...',
    LOADING_DESCRIPTION: 'Please wait while we fetch the data',
  },
  FILTER: {
    STATUS_ALL: 'All Statuses',
    DOMAIN_ALL: 'All Domains',
    SEARCH_PLACEHOLDER: 'Search by title...',
    SEARCH_ARIA_LABEL: 'Search knowledge sources',
    CLEAR_SEARCH_ARIA_LABEL: 'Clear search',
    REFRESH_BUTTON: 'Refresh',
    REFRESH_SUCCESS: 'Knowledge sources refreshed.',
    REFRESH_ERROR: 'Failed to refresh. Please try again.',
  },
  DETAIL: {
    DIALOG_TITLE: 'Knowledge Source Detail',
    CLOSE: 'Close',
    LOAD_ERROR: 'Failed to load source details. Please close and try again.',
    CURRENT_VERSION_LABEL: 'Current Version',
    UPLOAD_DATE_LABEL: 'Upload Date',
    APPROVAL_DATE_LABEL: 'Approval Date',
    UPLOADED_BY_LABEL: 'Uploaded By',
    STATUS_LABEL: 'Processing Status',
    INDEXED_ON_LABEL: 'Indexed On',
    TOTAL_VERSIONS_LABEL: 'Total Versions',
    DOMAINS_LABEL: 'Domains',
    CATEGORY_LABEL: 'Category',
    COUNTRY_LABEL: 'Country/Region',
    RETRY_SUCCESS: 'Retry ingestion triggered.',
    RETRY_ERROR: 'Failed to trigger retry. Please try again.',
    DELETE_VERSION_BUTTON_ARIA: (version: number) =>
      `Delete version ${version}`,
    DELETE_VERSION_CONFIRM_TITLE: 'Delete Version',
    DELETE_VERSION_CONFIRM_DESCRIPTION:
      'This version and its indexed file will be permanently removed. This action cannot be undone.',
    DELETE_VERSION_CONFIRM_BUTTON: 'Yes, delete',
    DELETE_VERSION_CANCEL_BUTTON: 'Cancel',
    DELETE_VERSION_SUCCESS: 'Knowledge source version deleted successfully.',
    DELETE_VERSION_ERROR: 'Failed to delete version. Please try again.',
    VERSION_HISTORY_TITLE: 'Version History',
    VERSION_HISTORY_COLUMNS: {
      VERSION: 'Version',
      STATUS: 'Status',
      FILE_NAME: 'File',
      INDEXED_ON: 'Indexed On',
      ACTIONS: '',
    },
    NO_INDEXED_DATE: '—',
    LOADING: 'Loading source details...',
  },
  DUPLICATE_WARN: {
    TITLE: 'Something went wrong',
    BODY: 'An error occurred while uploading. Please try again.',
    BACK_BUTTON: 'Go Back',
  },
  DOMAIN: {
    NAV_LABEL: 'Domain Groups',
    PAGE_TITLE: 'Domain Groups',
    PAGE_SUBTITLE: 'Create domain groups to organise knowledge sources.',
    CREATE_BUTTON: 'Create Domain Group',
    DELETE_BUTTON: 'Delete',
    CREATE_DIALOG_TITLE: 'Create Domain Group',
    CREATE_DIALOG_DESCRIPTION:
      'Define a new domain group for tagging knowledge sources.',
    FIELDS: {
      NAME_LABEL: 'Domain Name',
      NAME_PLACEHOLDER: 'e.g. Infant Nutrition',
      DESCRIPTION_LABEL: 'Description',
      DESCRIPTION_PLACEHOLDER: 'Optional description',
    },
    SUBMIT_BUTTON: 'Create',
    SUBMIT_LOADING: 'Creating...',
    CANCEL_BUTTON: 'Cancel',
    CREATE_SUCCESS: 'Domain group created successfully.',
    DELETE_SUCCESS: 'Domain group deleted.',
    CREATE_ERROR: 'Failed to create domain group. Please try again.',
    DELETE_ERROR: 'Failed to delete domain group. Please try again.',
    DELETE_CONFIRM_TITLE: 'Delete Domain Group',
    DELETE_CONFIRM_DESCRIPTION:
      'Documents tagged to this domain will revert to Unclassified. A domain that still has sub-domains cannot be deleted — the sub-domain count is shown in the table. This action cannot be undone.',
    DELETE_CONFIRM_BUTTON: 'Yes, delete',
    TABLE: {
      NO_COLUMN: 'No.',
      NAME_COLUMN: 'Name',
      DESCRIPTION_COLUMN: 'Description',
      SUB_DOMAINS_COLUMN: 'Sub-domains',
      ACTIVE_COLUMN: 'Active',
      ACTIVE_YES: 'Active',
      ACTIVE_NO: 'Inactive',
      CREATED_AT_COLUMN: 'Created At',
      EMPTY_TITLE: 'No domain groups yet',
      EMPTY_DESCRIPTION:
        'Create a domain group to organise your knowledge sources.',
      LOADING_TITLE: 'Loading domain groups...',
      LOADING_DESCRIPTION: 'Please wait while we fetch the data',
    },
  },
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load knowledge sources',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while loading the data. Please refresh the page or contact support.',
    DOMAIN_LOAD_FAILED_TITLE: 'Failed to load domain groups',
    DOMAIN_LOAD_FAILED_DESCRIPTION:
      'An error occurred while loading domain groups. Please refresh the page.',
  },
} as const;

/**
 * The domain endpoints answer failures with i18n keys rather than prose, and
 * `resolveApiMessage` deliberately hides keys behind generic local copy. These
 * map the codes we know about to copy that actually explains the failure.
 *
 * Keyed on the bare code, not the full key, so it resolves whether the backend
 * sends `message: 'error.DOMAIN.X'` or `error: { code: 'X' }`.
 */
export const DOMAIN_ERROR_MESSAGES: Record<string, string> = {
  CANNOT_DELETE_HAS_SUBDOMAINS:
    'This domain has sub-domains and cannot be deleted while they exist.',
  CANNOT_DELETE_IN_USE: 'This domain is still tagged to knowledge sources.',
  PARENT_NOT_FOUND: 'The selected parent domain no longer exists.',
  INVALID_PARENT_DOMAIN: 'The selected parent is not a valid main domain.',
};

export const SET_PASSWORD_STRINGS = {
  TITLE: 'Set Your Password',
  SUBTITLE: 'Create a secure password to activate your account',
  PASSWORD_LABEL: 'New Password',
  PASSWORD_PLACEHOLDER: 'Create a strong password',
  CONFIRM_PASSWORD_LABEL: 'Confirm Password',
  CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your password',
  SUBMIT_BUTTON: 'Set Password',
  LOADING_TEXT: 'Activating account...',
  SUCCESS_TITLE: 'Account Activated!',
  SUCCESS_MESSAGE:
    'Your account has been successfully activated. You can now sign in with your new password.',
  GO_TO_LOGIN: 'Go to Login',
  ERROR_INVALID_TOKEN:
    'Invalid or expired activation link. Please contact your administrator for a new invitation.',
  ERROR_GENERIC: 'Failed to activate account. Please try again.',
} as const;

export const OTP_STRINGS = {
  TITLE: 'Verify Your Email',
  SUBTITLE: 'Enter the verification code',
  DESCRIPTION: 'A 6-digit code has been sent to',
  DESCRIPTION_SUFFIX: 'Enter the code below to continue.',
  CODE_LABEL: 'Verification Code',
  SUBMIT_BUTTON: 'Verify Code',
  LOADING_TEXT: 'Verifying...',
  BACK_TO_LOGIN: 'Back to Login',
  RESEND_LABEL: "Didn't receive the code?",
  RESEND_BUTTON: 'Resend code',
  RESEND_COOLDOWN: 'Resend in {seconds}s',
  RESEND_SUCCESS: 'A new verification code has been sent to your email.',
  ERROR_INVALID: 'Invalid or expired code. Please try again.',
  ERROR_LOCKOUT:
    'Too many failed verification attempts. Please try again later or contact your administrator.',
  ERROR_GENERIC: 'Verification failed. Please try again.',
} as const;

export const REVIEWER_STRINGS = {
  PORTAL: {
    TITLE: 'Human Review Platform',
    SUBTITLE: 'Review Portal',
    DESCRIPTION: 'Manage and resolve escalated medical queries.',
  },
  LAYOUT: {
    LOGO_ALT: 'MAM Logo',
    PORTAL_TITLE: 'Review Portal',
    STATUS_ONLINE: 'Online',
    LOGOUT: 'Logout',
    LOGOUT_LOADING: 'Logging out…',
  },
  NAV: {
    SECTION_LABEL: 'Navigation',
    DASHBOARD: 'Dashboard',
  },
  DASHBOARD: {
    PAGE_TITLE: 'Dashboard',
    BREADCRUMB: 'Reviewer',
    STAT_TOTAL_QUERIES_LABEL: 'Total Queries',
    STAT_TOTAL_QUERIES_DESC: 'All review cases in the platform',
    STAT_COMPLETED_LABEL: 'Total Completed',
    STAT_COMPLETED_DESC: 'Cases with a final review outcome',
    STAT_PENDING_LABEL: 'Total Pending',
    STAT_PENDING_DESC: 'Cases awaiting review action',
    STAT_ASSIGNED_TO_ME_LABEL: 'Assigned to Me',
    STAT_ASSIGNED_TO_ME_DESC: 'Active cases in your queue',
    STAT_COMPLETED_BY_ME_LABEL: 'Completed by Me',
    STAT_COMPLETED_BY_ME_DESC: 'Cases you have reviewed',
    STAT_PENDING_ASSIGNED_TO_ME_LABEL: 'My Pending Cases',
    STAT_PENDING_ASSIGNED_TO_ME_DESC: 'Assigned cases awaiting completion',
    STAT_PROFESSIONAL_REVIEW_REQUESTS_LABEL: 'Professional Review Requests',
    STAT_PROFESSIONAL_REVIEW_REQUESTS_DESC:
      'Cases referred for professional review',
    QUEUE_SECTION_TITLE: 'Review Queue',
    EMPTY_STATE: 'No review cases are currently assigned to you.',
  },
  CASE_CARD: {
    RISK_LABEL: 'Risk',
    PRIORITY_LABEL: 'Priority',
    ASSIGNED_LABEL: 'Assigned',
    VIEW_BUTTON: 'View Case',
  },
  QUEUE_TABLE: {
    COL_NO: 'No.',
    COL_CASE_ID: 'Case ID',
    COL_QUERY: 'Query',
    COL_RISK: 'Risk',
    COL_PRIORITY: 'Priority',
    COL_STATUS: 'Status',
    COL_ASSIGNED_AT: 'Created At',
    COL_ASSIGNEE: 'Assigned To',
    COL_ACTIONS: 'Actions',
    COL_DOMAIN: 'Domain',
    COL_SCORE: 'Score',
    COL_FAILED_DIM: 'Failed',
    NO_ASSIGNEE: '—',
    ACTIONS_MENU_LABEL: 'Case actions',
    ACTION_VIEW: 'View Case',
    ACTION_PICK: 'Pick Case',
    ACTION_LEAVE: 'Leave Case',
    NO_DATA: 'No review cases are currently assigned to you.',
    LOADING_TITLE: 'Loading reviews…',
    NO_REVIEWS_TITLE: 'No reviews found',
    ERROR_TITLE: 'Failed to load reviews',
    ERROR_DESCRIPTION:
      'An error occurred while loading the review queue. Please try again.',
    LOCK_ICON_ARIA_LABEL: 'Case is locked by another reviewer',
    FILTER_SEARCH_PLACEHOLDER: 'Search by question',
    FILTER_SEARCH_ARIA_LABEL: 'Search cases by question',
    FILTER_CLEAR_SEARCH_ARIA_LABEL: 'Clear search',
    FILTER_RISK_ALL: 'All Risks',
    FILTER_RISK_CRITICAL: 'Critical',
    FILTER_RISK_HIGH: 'High',
    FILTER_RISK_MEDIUM: 'Medium',
    FILTER_RISK_LOW: 'Low',
    FILTER_PRIORITY_ALL: 'All Priorities',
    FILTER_PRIORITY_URGENT: 'Urgent',
    FILTER_PRIORITY_HIGH: 'High',
    FILTER_PRIORITY_NORMAL: 'Normal',
    FILTER_PRIORITY_LOW_MEDIUM: 'Low / Medium',
    FILTER_PRIORITY_CRITICAL: 'Critical',
    FILTER_PRIORITY_LOW: 'Low',
    FILTER_PRIORITY_MEDIUM: 'Medium',
    FILTER_ASSIGNMENT_ALL: 'All Cases',
    FILTER_ASSIGNMENT_MINE: 'Assigned to Me',
    FILTER_ASSIGNMENT_UNASSIGNED: 'Unassigned',
    FILTER_STATUS_ALL: 'All Status',
    FILTER_STATUS_APPROVED: 'Approved',
    FILTER_STATUS_EDITED_AND_APPROVED: 'Edited & Approved',
    FILTER_STATUS_REJECTED: 'Rejected',
    NO_DATA_FILTERED: 'No cases match your current filters.',
    SORT_NEWEST: 'Newest first',
    SORT_OLDEST: 'Oldest first',
    ACTION_PICKING: 'Picking…',
    ACTION_LEAVING: 'Leaving…',
    PICK_SUCCESS: 'Case assigned to you successfully.',
    PICK_ERROR: 'Failed to assign case. Please try again.',
    LEAVE_SUCCESS: 'Review unassigned successfully.',
    LEAVE_ERROR: 'Failed to unassign review. Please try again.',
  },
  FEEDBACK_DIALOG: {
    TITLE: 'Case Feedback',
    DESCRIPTION:
      'Rate each evaluation dimension and leave any additional observations.',
    DIMENSION_RATINGS_LABEL: 'Dimension Ratings',
    DIMENSION_RATING_ARIA_LABEL: 'Rate {dimension} {star} stars',
    ADDITIONAL_NOTES_LABEL: 'Additional Notes',
    ADDITIONAL_NOTES_PLACEHOLDER:
      'Share your expert observations on this case…',
    ADDITIONAL_NOTES_CHAR_LIMIT: 1000,
    SYSTEMATIC_ISSUE_LABEL: 'Systematic Issue',
    SYSTEMATIC_ISSUE_DESC:
      'This problem reflects a broader retrieval or prompt issue, not an isolated error.',
    SUBMIT_BUTTON: 'Submit Feedback',
    SKIP_BUTTON: 'Skip',
    SUCCESS_MESSAGE: 'Feedback submitted.',
    SKIPPED_NOTE: 'Feedback was not provided for this case.',
    SUBMIT_ERROR: 'Failed to submit feedback. Please try again.',
    VALIDATION_ERROR: 'Please provide ratings for all dimensions (1-5).',
    QUERY_LABEL: 'Original Query',
    AI_RESPONSE_LABEL: 'AI Response',
  },
  CASE_DIALOG: {
    TITLE_PREFIX: 'Review Case',
    QUERY_SECTION: 'Original Query',
    RESPONSE_SECTION: 'AI-Generated Response',
    EVALUATION_SECTION: 'Evaluation Results',
    ROUTING_REASON_LABEL: 'Routing Reason',
    SCORE_LABEL: 'Score',
    THRESHOLD_LABEL: 'Threshold',
    PASS: 'Pass',
    FAIL: 'Fail',
    BORDERLINE: 'Borderline',
    SOURCES_SECTION: 'Source Documents',
    SOURCES_UNAVAILABLE: 'Source details unavailable for this case',
    RISK_LABEL: 'Risk Category',
    PRIORITY_LABEL: 'Priority',
    STATUS_LABEL: 'Status',
    SUBMITTED_LABEL: 'Submitted',
    APPROVE_BUTTON: 'Approve',
    REJECT_BUTTON: 'Reject',
    EDIT_BUTTON: 'Edit Response',
    EDIT_RESPONSE_LABEL: 'Edit AI Response',
    EDIT_SAVE_BUTTON: 'Submit Response',
    EDIT_SAVING_BUTTON: 'Submitting…',
    MODIFY_CONFIRM_TITLE: 'Submit Edited Response',
    MODIFY_CONFIRM_DESC:
      'Confirm that you are satisfied with your edited response. Once submitted, this action cannot be undone.',
    MODIFY_CONFIRM_BUTTON: 'Yes, Submit',
    EDIT_CANCEL_BUTTON: 'Cancel',
    APPROVE_CONFIRM_TITLE: 'Confirm Approval',
    APPROVE_CONFIRM_DESC:
      'Please confirm you have reviewed the complete response before approving.',
    APPROVE_CONFIRM_BUTTON: 'Confirm Approval',
    REJECT_CONFIRM_TITLE: 'Reject Review',
    REJECT_CONFIRM_DESC:
      'Please provide a reason and details for rejecting this review.',
    REJECT_CONFIRM_BUTTON: 'Confirm Rejection',
    REJECTION_REASON_LABEL: 'Rejection Reason',
    REJECTION_REASON_OPTIONS: {
      FACTUALLY_INCORRECT: 'Factually Incorrect',
      INAPPROPRIATE_TONE: 'Inappropriate Tone',
      OUT_OF_SCOPE: 'Out of Scope',
      SAFETY_RISK: 'Safety Risk',
      OTHER: 'Other',
    },
    REJECTION_DETAILS_LABEL: 'Rejection Details',
    REJECTION_REASON_PLACEHOLDER: 'Select a reason...',
    REJECTION_DETAILS_PLACEHOLDER:
      'Explain why you are rejecting this review...',
    CANCEL_BUTTON: 'Cancel',
    ACTION_NOT_AVAILABLE:
      'This action will be available when the API is ready.',
    APPROVE_ERROR: 'Failed to approve case. Please try again.',
    MODIFY_ERROR: 'Failed to modify response. Please try again.',
    REJECT_ERROR: 'Failed to reject case. Please try again.',
    LOADING_TITLE: 'Loading review details…',
    ERROR_TITLE: 'Failed to load review details',
    ERROR_DESCRIPTION: 'Please close and try again.',
    OVERALL_SCORE_LABEL: 'Overall Score',
    CONFIDENCE_LABEL: 'Confidence',
    DOMAIN_LABEL: 'Domain',
    RATIONALE_LABEL: 'Rationale',
    API_PRIORITY_LABELS: {
      CRITICAL: 'Critical',
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
    },
    API_STATUS_LABELS: {
      PENDING: 'Pending',
      IN_PROGRESS: 'In Progress',
      APPROVED: 'Approved',
      EDITED_AND_APPROVED: 'Edited & Approved',
      REJECTED: 'Rejected',
    },
    PICK_REQUIRED_TOOLTIP: 'Pick this case to take action',
    PICK_BUTTON: 'Pick Case',
    PICK_CASE_HINT: 'Pick this case to enable review actions',
    PICKING_BUTTON: 'Picking...',
    LEAVE_BUTTON: 'Leave Case',
    LEAVING_BUTTON: 'Leaving...',
    ORIGINAL_RESPONSE_SECTION: 'Original AI Response',
    MODIFIED_RESPONSE_SECTION: 'Edited Response',
    REJECTION_INFO_SECTION: 'Rejection Information',
    REJECTION_REASON_DISPLAY_LABEL: 'Reason',
    REJECTION_DETAILS_DISPLAY_LABEL: 'Details',
  },
} as const;

// Export individual groups for convenience
export const { LOGIN, REGISTER, COMMON: AUTH_COMMON } = AUTH_STRINGS;

export const WELCOME_HERO_STRINGS = {
  GREETING: 'Hello,',
  SUBHEADING: 'How are you feeling today?',
  SUGGESTIONS: [
    {
      title: 'Is my baby feeding enough?',
      subtitle: 'Signs of a good feed',
    },
    {
      title: 'How do I survive the nights?',
      subtitle: 'Sleep with a newborn',
    },
    {
      title: "What's normal after birth?",
      subtitle: 'Bleeding, healing, hormones',
    },
    {
      title: 'I feel overwhelmed.',
      subtitle: 'Looking after yourself too',
    },
  ],
  DISCLAIMER:
    'We offer gentle guidance, not medical advice. Always consult your healthcare provider for medical concerns.',
} as const;

export const CHAT_PAGE_STRINGS = {
  MENU_LABEL: 'Open menu',
  SIDEBAR_OPEN_LABEL: 'Open sidebar',
  MENU_ASSISTANT: 'Assistant',
  MENU_VOICE_JOURNAL: 'Voice journal',
  MENU_CLEAR_CHAT: 'Clear chat',
  MENU_PROFILE: 'Profile & Settings',
  MENU_PRIVACY_CENTER: 'Privacy Center',
  MENU_LOG_OUT: 'Log out',
  MENU_CONSENT_REQUIRED_HINT: 'Restore consent to use this',
  SIDEBAR_TITLE: 'AI Parent Companion',
  SIDEBAR_TAGLINE: 'Your parenting companion',
  SIDEBAR_RECENT: 'Recent Conversations',
  NEW_CHAT: 'New chat',
  SIDEBAR_EMPTY: 'Start a new conversation',
  SIDEBAR_NEW_CONVERSATION: 'New conversation',
  SIDEBAR_REMOVE_SESSION: 'Remove from recent',
  SIDEBAR_LOGO_ALT: 'MamBaby logo',
} as const;

export const NOTIFICATION_STRINGS = {
  BELL_TITLE: 'Notifications',
  MARK_ALL_READ: 'Mark all as read',
  EMPTY: 'No new notifications',
  APPROVED_LABEL: 'Your answer is ready',
  MODIFIED_TITLE: 'Response Updated',
  MODIFIED_BODY:
    'Your response has been updated by a reviewer. Tap to view the changes.',
  MARK_READ_ERROR: 'Failed to mark notification as read',
  MARK_ALL_READ_ERROR: 'Failed to mark all notifications as read',
  RELATIVE_TIME: {
    NOW: 'now',
    JUST_NOW: 'Just now',
    MINUTES_AGO: (minutes: number) => `${minutes}m ago`,
    HOURS_AGO: (hours: number) => `${hours}h ago`,
    DAYS_AGO: (days: number) => `${days}d ago`,
    YESTERDAY: 'Yesterday',
  },
} as const;

export const CHANGE_PASSWORD_STRINGS = {
  MENU_ITEM: 'Change Password',
  DIALOG_TITLE: 'Change Password',
  DIALOG_DESCRIPTION: 'Enter your current password and choose a new one.',
  OLD_PASSWORD_LABEL: 'Old Password',
  OLD_PASSWORD_PLACEHOLDER: 'Enter your old password',
  OLD_PASSWORD_TOGGLE_ARIA: 'Toggle old password visibility',
  NEW_PASSWORD_LABEL: 'New Password',
  NEW_PASSWORD_PLACEHOLDER: 'Enter your new password',
  NEW_PASSWORD_TOGGLE_ARIA: 'Toggle new password visibility',
  CONFIRM_PASSWORD_LABEL: 'Confirm New Password',
  CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your new password',
  CONFIRM_PASSWORD_TOGGLE_ARIA: 'Toggle confirm password visibility',
  SUBMIT_BUTTON: 'Change Password',
  SUBMIT_LOADING: 'Changing…',
  CANCEL_BUTTON: 'Cancel',
  SUCCESS_MESSAGE: 'Password changed successfully.',
  ERROR_GENERIC: 'Failed to change password. Please try again.',
} as const;

export const PROFILE_STRINGS = {
  PAGE_TITLE: 'Profile & Settings',
  PAGE_SUBTITLE: 'Manage your account, security, and privacy preferences.',
  BACK_LABEL: 'Back',
  ACCOUNT: {
    SECTION_TITLE: 'Account Information',
    SECTION_DESCRIPTION: 'Your personal details.',
    NAME_LABEL: 'Full Name',
    EMAIL_LABEL: 'Email Address',
    COUNTRY_LABEL: 'Country',
    STATE_LABEL: 'State',
    NOT_PROVIDED: 'Not provided',
  },
  SECURITY: {
    SECTION_TITLE: 'Security',
    SECTION_DESCRIPTION: 'Keep your account secure.',
    CHANGE_PASSWORD_BUTTON: 'Change Password',
  },
} as const;

export const PRIVACY_STRINGS = {
  PAGE_TITLE: 'Privacy Center',
  PAGE_SUBTITLE:
    'Browse, view, and download the data we hold about you across journals, chats, and reviews.',
  BACK_LABEL: 'Back',
  SEARCH_PLACEHOLDER: 'Search…',
  TABS: {
    JOURNALS: 'Journals',
    CHATS: 'Chat History',
    REVIEWS: 'Review History',
  },
  COLUMNS: {
    NO: 'No.',
    DATE: 'Date',
    ID: 'ID',
    SESSION_ID: 'Session ID',
    TRANSCRIPTION: 'Transcription',
    REVIEW_ID: 'Review ID',
    QUESTION: 'Question',
    STATUS: 'Status',
  },
  JOURNALS: {
    EMPTY_TITLE: 'No journals yet',
    EMPTY_DESCRIPTION: 'Your voice journal entries will appear here.',
    LOADING: 'Loading your journals…',
  },
  CHATS: {
    EMPTY_TITLE: 'No chat history',
    EMPTY_DESCRIPTION:
      'Your conversations with the assistant will appear here.',
    LOADING: 'Loading your chat history…',
  },
  REVIEWS: {
    EMPTY_TITLE: 'No review history',
    EMPTY_DESCRIPTION: 'Answers sent for human review will appear here.',
    LOADING: 'Loading your review history…',
    SEARCH_PLACEHOLDER: 'Search by question…',
  },
  STATUS: {
    COMPLETED: 'Completed',
    PENDING: 'Pending',
    REJECTED: 'Rejected',
  },
  DIALOG: {
    JOURNAL_TITLE: 'Journal Entry',
    CHAT_TITLE: 'Chat Transcript',
    CHAT_SESSION_ID_LABEL: 'Session ID',
    REVIEW_TITLE: 'Review Details',
    CLOSE: 'Close',
    LOADING: 'Loading…',
    LOAD_ERROR: 'Failed to load. Please close and try again.',
    NOT_FOUND: 'This record could not be found. It may have been removed.',
    REVIEW: {
      QUESTION: 'Original Query',
      STATUS: 'Status',
      APPROVED_RESPONSE: "Reviewer's Response",
      NO_RESPONSE: 'No reviewer response has been provided yet.',
      REJECTED_RESPONSE:
        'The reviewer is unable to answer your query at this time.',
      REVIEWED_AT: 'Reviewed At',
      NOT_REVIEWED: 'Not yet reviewed',
    },
    CHAT: {
      USER: 'You',
      ASSISTANT: 'MamBaby AI',
      FEEDBACK_POSITIVE: 'You liked this response.',
      FEEDBACK_POSITIVE_WITH_COMMENT:
        'You liked this response and added a comment.',
      FEEDBACK_NEGATIVE: 'You disliked this response.',
      FEEDBACK_NEGATIVE_WITH_COMMENT:
        'You disliked this response and added a comment.',
      NO_FEEDBACK: 'No feedback yet',
      EMPTY: 'No messages in this conversation.',
    },
  },
  DOWNLOAD: {
    BUTTON: 'Download',
    GENERATING: 'Preparing…',
    SUCCESS: 'Your download has started.',
    ERROR: 'Failed to prepare your download. Please try again.',
    FILE_JOURNAL: 'journal',
    FILE_CHAT: 'chat',
    FILE_REVIEW: 'review',
  },
} as const;

export const CONSENT_STRINGS = {
  SECTION_TITLE: 'Privacy & Consent',
  SECTION_DESCRIPTION:
    'Control how your personal and health data is processed under GDPR.',
  STATUS_LABEL: 'Consent status',
  STATUS_GRANTED: 'Granted',
  STATUS_WITHDRAWN: 'Withdrawn',
  STATUS_NOT_GRANTED: 'Not consented',
  GRANTED_HELP:
    'Your consent is active. Assistant, Voice Journal, and Notifications are available.',
  WITHDRAWN_HELP:
    'You have withdrawn consent. New data processing is paused and the Assistant, Voice Journal, and Notifications are disabled until you restore it.',
  NOT_GRANTED_HELP:
    'You have not yet provided consent. The Assistant, Voice Journal, and Notifications are unavailable until you grant consent.',
  WITHDRAW_BUTTON: 'Withdraw consent',
  RESTORE_BUTTON: 'Restore consent',
  RESTORING_LABEL: 'Restoring…',
  // Withdrawal confirmation dialog
  DIALOG_TITLE: 'Withdraw consent?',
  DIALOG_DESCRIPTION:
    'Withdrawing consent stops processing of your personal and health data. The Assistant, Voice Journal, and Notifications will be disabled. You can restore consent later from this page.',
  DIALOG_CONFIRM_BUTTON: 'Yes, withdraw',
  DIALOG_CONFIRMING_LABEL: 'Withdrawing…',
  DIALOG_CANCEL_BUTTON: 'Cancel',
  // Optional withdrawal reason
  REASON_LABEL: 'Reason (optional)',
  REASON_PLACEHOLDER: 'Let us know why you are withdrawing consent…',
  REASON_CHAR_COUNT: '{count} / {limit}',
  // Toasts
  WITHDRAW_SUCCESS:
    'Your consent has been withdrawn. Data processing has stopped.',
  WITHDRAW_ERROR: 'Failed to withdraw consent. Please try again.',
  RESTORE_SUCCESS:
    'Your consent has been restored. Features are available again.',
  RESTORE_ERROR: 'Failed to restore consent. Please try again.',
  // Blocked-state notice shown in place of gated features
  NOTICE_TITLE: 'Consent required',
  NOTICE_BODY:
    'You have withdrawn consent, so this feature is paused. Restore your consent from your profile to continue.',
  NOTICE_CTA: 'Go to Profile',
  // Global "consent required" modal (triggered by a 403 from a gated action)
  MODAL_TITLE: 'Consent required',
  MODAL_BODY:
    'This action needs your consent, which is currently withdrawn. Restore your consent from your profile to continue.',
  MODAL_CANCEL: 'Cancel',
  MODAL_GO_TO_PROFILE: 'Go to Profile',
} as const;

export const FORGOT_PASSWORD_STRINGS = {
  TITLE: 'Forgot Password?',
  SUBTITLE: "Enter your email and we'll send you a reset link.",
  EMAIL_LABEL: 'Email Address',
  EMAIL_PLACEHOLDER: 'Enter your email address',
  SUBMIT_BUTTON: 'Send Reset Link',
  LOADING_TEXT: 'Sending…',
  SUCCESS_TITLE: 'Check Your Email',
  SUCCESS_MESSAGE:
    'If an account exists for that email, you will receive a reset link shortly.',
  BACK_TO_LOGIN: 'Back to Login',
  ERROR_GENERIC: 'Failed to send reset link. Please try again.',
} as const;

export const RESET_PASSWORD_STRINGS = {
  TITLE: 'Reset Your Password',
  SUBTITLE: 'Create a strong new password for your account.',
  NEW_PASSWORD_LABEL: 'New Password',
  NEW_PASSWORD_PLACEHOLDER: 'Create a strong password',
  NEW_PASSWORD_TOGGLE_ARIA: 'Toggle password visibility',
  CONFIRM_PASSWORD_LABEL: 'Confirm Password',
  CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your new password',
  CONFIRM_PASSWORD_TOGGLE_ARIA: 'Toggle confirm password visibility',
  SUBMIT_BUTTON: 'Reset Password',
  LOADING_TEXT: 'Resetting…',
  SUCCESS_TITLE: 'Password Reset!',
  SUCCESS_MESSAGE:
    'Your password has been updated. Please log in with your new password.',
  GO_TO_LOGIN: 'Go to Login',
  REQUEST_NEW_LINK: 'Request a new reset link',
  ERROR_INVALID_TOKEN:
    'This reset link is invalid or has expired. Please request a new one.',
  ERROR_GENERIC: 'Failed to reset password. Please try again.',
} as const;

export const LAYOUT_STRINGS = {
  ACCOUNT_MENU_ARIA: 'Open account menu',
  MOBILE_MENU_ARIA: 'Open navigation menu',
} as const;

export const AI_COST_STRINGS = {
  NAV: { LABEL: 'AI Cost Monitoring' },
  PAGE_TITLE: 'AI Cost Monitoring',
  PAGE_DESCRIPTION:
    'Azure Monitor AI token usage and cost metrics for all processing stages.',
  BREADCRUMB: 'AI Cost Monitoring',
  DATE_FILTER: {
    START_LABEL: 'Start Date',
    END_LABEL: 'End Date',
    PRESET_TODAY: 'Today',
    PRESET_LAST_7: 'Last 7 Days',
    PRESET_LAST_30: 'Last 30 Days',
    PRESET_CURRENT_MONTH: 'Current Month',
    PRESET_CUSTOM: 'Custom',
    LAST_REFRESHED: 'Last refreshed',
    REFRESH_BUTTON: 'Refresh',
  },
  METRICS: {
    TOTAL_TOKENS: 'Total Tokens',
    TOTAL_COST: 'Total Cost (USD)',
    TOTAL_RESPONSES: 'Total Responses',
    AVG_COST_PER_RESPONSE: 'Avg Cost / Response',
    AVG_TOKENS_PER_RESPONSE: 'Avg Tokens / Response',
  },
  CHART: {
    TOKENS_TITLE: 'Token Usage by Stage',
    COST_TITLE: 'Cost by Stage (USD)',
    GENERATION_LABEL: 'Generation',
    EVALUATION_LABEL: 'Evaluation',
  },
  ERROR: {
    UNAVAILABLE:
      'Cost data temporarily unavailable. Please check Azure Monitor directly or try again shortly.',
    LOAD_FAILED_TITLE: 'Failed to load cost data',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching AI cost analytics.',
  },
} as const;

export const VALIDATION_STRINGS = {
  NAV_LABEL: 'Validation Control Dashboard',
  PAGE_TITLE: 'Validation Control',
  PAGE_DESCRIPTION:
    'Monitor the health of the automated validation pipeline — track pass, fail, escalation, and delivery outcomes.',
  BREADCRUMB: 'Validation Control',
  DATE_FILTER: {
    START_LABEL: 'Start Date',
    END_LABEL: 'End Date',
    PRESET_TODAY: 'Today',
    PRESET_LAST_7: 'Last 7 Days',
    PRESET_LAST_30: 'Last 30 Days',
    PRESET_CURRENT_MONTH: 'Current Month',
    PRESET_CUSTOM: 'Custom',
    LAST_REFRESHED: 'Last refreshed',
    REFRESH_BUTTON: 'Refresh',
  },
  STATUS_CARDS: {
    PASS: 'Pass',
    FAILED: 'Failed',
    ESCALATED: 'Escalated',
    UNDER_REVIEW: 'Under Review',
    DELIVERED: 'Delivered',
    REJECTED: 'Rejected',
    JOURNAL_ENTRY: 'Journal Entry',
    REFER_PROFESSIONAL: 'Refer to Professional',
    CLICK_TO_FILTER: 'Click to filter',
    CLICK_TO_CLEAR: 'Click to clear filter',
  },
  TABLE: {
    NO_COLUMN: 'No.',
    QUERY_COLUMN: 'Query',
    STATUS_COLUMN: 'Status',
    RISK_COLUMN: 'Risk',
    CONFIDENCE_COLUMN: 'Confidence',
    ROUTING_COLUMN: 'Routing Decision',
    SUBMITTED_AT_COLUMN: 'Submitted At',
    ACTIONS_COLUMN: '',
    VIEW_DETAILS: 'View Details',
    SEARCH_PLACEHOLDER: 'Search by Query',
    SEARCH_LABEL: 'Search queries',
    SEARCH_CLEAR: 'Clear search',
    VIEW_DETAILS_ARIA: 'View details for query',
    LOADING: 'Loading queries…',
    LOADING_DESCRIPTION: 'Fetching validation pipeline data.',
    NO_DATA: 'No queries found',
    NO_DATA_DESCRIPTION: 'No queries match the current filters.',
    ERROR: 'Failed to load queries',
  },
  FILTER: {
    STATUS_ALL: 'All Statuses',
    STATUS_PASS: 'Pass',
    STATUS_FAILED: 'Failed',
    STATUS_ESCALATED: 'Escalated',
    STATUS_UNDER_REVIEW: 'Under Review',
    STATUS_DELIVERED: 'Delivered',
    STATUS_REJECTED: 'Rejected',
    STATUS_REFER_PROFESSIONAL: 'Refer to Professional',
  },
  DETAIL_DIALOG: {
    TITLE: 'Query Details',
    MESSAGE_ID_LABEL: 'Message ID',
    SUBMITTED_AT_LABEL: 'Submitted At',
    QUERY_STATUS_LABEL: 'Query Status',
    DASHBOARD_STATUS_LABEL: 'Pipeline Status',
    CONFIDENCE_SCORE_LABEL: 'Confidence Score',
    SECTION_QUERY: 'Query',
    SECTION_EVALUATION: 'Evaluation',
    OVERALL_SCORE_LABEL: 'Overall Score',
    ROUTING_DECISION_LABEL: 'Routing Decision',
    RISK_CATEGORY_LABEL: 'Risk Category',
    FAILED_DIMENSIONS_LABEL: 'Failed Dimensions',
    NO_FAILED_DIMENSIONS: 'None',
    SECTION_REVIEW: 'Reviewer Action',
    NO_REVIEW: 'No reviewer action recorded.',
    CLOSE_BUTTON: 'Close',
    JOURNEY_TITLE: 'Query Journey – End to End',
    JOURNEY_SUBTITLE:
      'Visualizing the life cycle of a user query on the platform',
    LEGEND_USER: 'User / System',
    LEGEND_AI: 'AI',
    LEGEND_HUMAN: 'Human Review',
    LEGEND_OUTCOME: 'Outcome',
    STAGES_INVOLVED: 'Stages Involved',
    STAGES_COUNT_SUFFIX: 'Stages',
    CASE1_LABEL: 'CASE 1',
    CASE1_TITLE: 'Not Flagged – Direct AI Response (2 Stages)',
    CASE2B_LABEL: 'CASE 2B',
    CASE2B_TITLE: 'Flagged by AI – User Chooses Not to Send',
    CASE2A_P1_LABEL: 'CASE 2A – PATH 1',
    CASE2A_P1_TITLE:
      'Flagged by AI – User Chooses to Send for Human Review → Approved',
    CASE2A_P2_LABEL: 'CASE 2A – PATH 2',
    CASE2A_P2_TITLE:
      'Flagged by AI – User Chooses to Send for Human Review → Edited & Approved',
    CASE2A_P3_LABEL: 'CASE 2A – PATH 3',
    CASE2A_P3_TITLE:
      'Flagged by AI – User Chooses to Send for Human Review → Rejected',
    IN_PROGRESS_LABEL: 'IN PROGRESS',
    IN_PROGRESS_TITLE: 'Flagged by AI – Awaiting Human Review',
    CASE1_REVIEW_P1_LABEL: 'CASE 1 – REVIEW',
    CASE1_REVIEW_P1_TITLE: 'Not Flagged – User Requested Review → Approved',
    CASE1_REVIEW_P2_LABEL: 'CASE 1 – REVIEW',
    CASE1_REVIEW_P2_TITLE:
      'Not Flagged – User Requested Review → Edited & Approved',
    CASE1_REVIEW_P3_LABEL: 'CASE 1 – REVIEW',
    CASE1_REVIEW_P3_TITLE: 'Not Flagged – User Requested Review → Rejected',
    CASE1_REVIEW_IN_PROGRESS_LABEL: 'IN PROGRESS',
    CASE1_REVIEW_IN_PROGRESS_TITLE:
      'Not Flagged – User Requested Review → Awaiting Review',
    JOURNAL_ACCEPTED_LABEL: 'JOURNAL SAVED',
    JOURNAL_ACCEPTED_TITLE: 'Journal Entry – User Chose to Save',
    JOURNAL_DECLINED_LABEL: 'JOURNAL SKIPPED',
    JOURNAL_DECLINED_TITLE: 'Journal Entry – User Chose Not to Save',
    FAILED_LABEL: 'FAILED',
    FAILED_TITLE: 'Processing Failed',
    STAGE_FAILED: 'Processing Failed',
    STAGE_FAILED_SUB: 'Query could not be processed',
    SAFETY_BLOCKED_LABEL: 'SAFETY BLOCKED',
    SAFETY_BLOCKED_TITLE: 'Blocked by Safety Guardrail',
    STAGE_SAFETY_BLOCKED: 'Safety Blocked',
    STAGE_SAFETY_BLOCKED_SUB: 'Blocked by AI safety guardrail',
    STAGE_USER_ASKS: 'User Asks a Query',
    STAGE_USER_ASKS_SUB: 'Query submitted by end user',
    STAGE_AI_PROCESSES: 'AI Processes & Responds',
    STAGE_AI_PROCESSES_SUB: 'Query not flagged by AI',
    STAGE_AI_FLAGS: 'AI Processes & Flags',
    STAGE_AI_FLAGS_SUB: 'Query flagged by AI for human review',
    STAGE_USER_CHOICE: 'User Choice',
    STAGE_USER_CHOICE_SUB: 'User chooses not to send',
    STAGE_USER_CHOICE_SEND_SUB: 'User chooses to send for human review',
    STAGE_GENERIC_RESPONSE: 'Generic Response Shown',
    STAGE_GENERIC_RESPONSE_SUB: 'User prompted to ask another question',
    STAGE_JOURNAL_DETECTED: 'Detected as Journal',
    STAGE_JOURNAL_DETECTED_SUB: 'AI identified query as a journal entry',
    STAGE_JOURNAL_CHOICE_YES_SUB: 'User chose to save journal entry',
    STAGE_JOURNAL_CHOICE_NO_SUB: 'User chose not to save journal entry',
    STAGE_JOURNAL_RECORDED: 'Journal Entry Recorded',
    STAGE_JOURNAL_RECORDED_SUB: 'Relevant response delivered',
    STAGE_JOURNAL_GENERIC: 'Relevant Response Delivered',
    STAGE_JOURNAL_GENERIC_SUB: 'Relevant response delivered',
    STAGE_DELIVERED: 'Response Delivered to User',
    STAGE_DELIVERED_SUB: 'AI response shown to the user',
    STAGE_USER_REQUESTS_REVIEW: 'User Requests Review',
    STAGE_USER_REQUESTS_REVIEW_SUB: 'User requests review despite approval',
    STAGE_HUMAN_REVIEW: 'Human Review',
    STAGE_HUMAN_REVIEW_SUB: 'Reviewer checks query & AI response',
    STAGE_REVIEW_DECISION: 'Review Decision',
    STAGE_APPROVED: 'Approved',
    STAGE_APPROVED_SUB: 'Final response sent to user',
    STAGE_EDITED_APPROVED: 'Edited & Approved',
    STAGE_EDITED_APPROVED_SUB: 'Edited response sent to user',
    STAGE_REJECTED: 'Rejected',
    STAGE_REJECTED_SUB: 'No response delivered to user',
    ARROW_NO_DONT_SEND: "No, Don't Send",
    ARROW_YES_SEND: 'Yes, Send for Review',
    ARROW_REQUEST_REVIEW: 'Requests Review',
    ARROW_YES_SAVE: 'Yes, Save',
    ARROW_NO_SKIP: 'No, Skip',
    AI_RESPONSE_UNAVAILABLE: 'AI response is not available for this query.',
    SECTION_AI_RESPONSE: 'AI Response',
    SECTION_DIMENSION_SCORES: 'Dimension Scores',
    SECTION_AUDIT_TRAIL: 'Audit Trail',
    SECTION_LIFECYCLE: 'Lifecycle',
    AI_RESPONSE_LABEL: 'Generated Answer',
    SHORT_ANSWER_LABEL: 'Summary',
    WHAT_THIS_MEANS_LABEL: 'What This Means',
    WHAT_TO_DO_NOW_LABEL: 'What To Do Now',
    WHAT_TO_WATCH_FOR_LABEL: 'What To Watch For',
    WHEN_TO_GET_HELP_LABEL: 'When To Get Help',
    SOURCES_LABEL: 'Sources',
    TOKEN_USAGE_LABEL: 'Token Usage',
    COST_LABEL: 'Cost (USD)',
    MODEL_LABEL: 'Model',
    THRESHOLD_LABEL: 'Threshold',
    DIMENSION_LABEL: 'Dimension',
    SCORE_LABEL: 'Score | Threshold',
    PASSED_LABEL: 'Passed',
    RATIONALE_LABEL: 'Rationale',
    ROUTING_REASON_LABEL: 'Routing Reason',
    REVIEWER_LABEL: 'Reviewer',
    REVIEW_STATUS_LABEL: 'Review Status',
    ORIGINAL_ANSWER_LABEL: 'Original AI Answer',
    MODIFIED_ANSWER_LABEL: 'Edited Response',
    EDIT_SUMMARY_LABEL: 'Edit Summary',
    REVIEW_DURATION_LABEL: 'Review Duration',
    DURATION_LABEL: 'Total Duration',
    DETAIL_LOADING: 'Loading query details…',
    COMPLETED_AT_LABEL: 'Completed At',
    REVIEWED_AT_LABEL: 'Reviewed At',
    STAGE_REVIEW_DECISION_APPROVE: 'Approve',
    STAGE_REVIEW_DECISION_EDIT_APPROVE: 'Edit & Approve',
    STAGE_REVIEW_DECISION_REJECT: 'Reject',
    SECTION_REJECTION: 'Rejection Information',
    REJECTION_REASON_LABEL: 'Reason',
    REJECTION_DETAILS_LABEL: 'Details',
  },
  AUDIT_ACTION_LABELS: {
    QUERY_SUBMITTED: 'Query Submitted',
    FLAGGED_FOR_HUMAN_REVIEW: 'AI Flagged for Human Review',
    USER_DECLINED_REVIEW: 'User Declined Human Review',
    USER_SENT_FOR_REVIEW: 'User Sent for Human Review',
    REVIEW_CREATED: 'Review Created',
    ASSIGNED: 'Assigned to Reviewer',
    APPROVED: 'Approved by Reviewer',
    EDITED_AND_APPROVED: 'Edited & Approved by Reviewer',
    REJECTED: 'Rejected by Reviewer',
    AUTO_APPROVED: 'Auto-Approved by System',
  },
  STATUS_LABEL: {
    PASS: 'Pass',
    FAILED: 'Failed',
    ESCALATED: 'Escalated',
    UNDER_REVIEW: 'Under Review',
    DELIVERED: 'Delivered',
    REJECTED: 'Rejected',
    JOURNAL_ENTRY: 'Journal Entry',
    REFER_PROFESSIONAL: 'Refer Professional',
  },
  ROUTING_LABEL: {
    MANUAL_REVIEW: 'Manual Review',
    AUTO_APPROVED: 'Auto Approved',
    AUTO_REJECTED: 'Auto Rejected',
    OUT_OF_SCOPE: 'Out of Scope',
    SYSTEM_ERROR: 'System Error',
    SAFETY_BLOCKED: 'Safety Blocked',
  },
  ERROR: {
    STATS_UNAVAILABLE:
      'Pipeline stats temporarily unavailable. Please try again shortly.',
    LOAD_FAILED_TITLE: 'Failed to load validation data',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching validation pipeline data.',
    EVALUATION_UNAVAILABLE: 'Evaluation data is not available for this query.',
  },
} as const;

export const QUOTA_RULES_STRINGS = {
  NAV: { LABEL: 'Cost Controls' },
  PAGE_TITLE: 'Cost Controls',
  PAGE_DESCRIPTION:
    'Configure AI usage quotas and throttling rules to control costs without code deployments.',
  BREADCRUMB: 'Cost Controls',
  ADD_RULE_BUTTON: 'Add Rule',
  TABLE: {
    TARGET_COLUMN: 'Rule Target',
    METRIC_COLUMN: 'Metric',
    PERIOD_COLUMN: 'Period',
    LIMIT_COLUMN: 'Limit',
    CREATED_COLUMN: 'Created',
    ACTIONS_COLUMN: '',
    LOADING: 'Loading quota rules…',
    NO_DATA: 'No quota rules configured',
    NO_DATA_DESCRIPTION:
      'Add a rule to start throttling AI usage based on cost, tokens, or requests.',
    ERROR: 'Failed to load quota rules',
    ERROR_DESCRIPTION: 'Refresh the page or try again in a moment.',
  },
  TARGET_LABELS: {
    user_queries: 'User Queries',
    ingestion_ops: 'Ingestion Operations',
  } as const,
  METRIC_LABELS: {
    cost: 'Cost (USD)',
    tokens: 'Tokens',
    requests: 'Requests',
  } as const,
  METRIC_UNITS: {
    cost: 'USD',
    tokens: 'tokens',
    requests: 'requests',
  } as const,
  PERIOD_LABELS: {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  } as const,
  FORM: {
    TARGET_LABEL: 'Target',
    TARGET_PLACEHOLDER: 'Select target',
    METRIC_LABEL: 'Metric',
    METRIC_PLACEHOLDER: 'Select metric',
    PERIOD_LABEL: 'Period',
    PERIOD_PLACEHOLDER: 'Select period',
    LIMIT_LABEL: 'Limit',
    LIMIT_PLACEHOLDER: 'Enter limit value',
    LIMIT_HINT_COST: 'Value in USD (e.g. 500 = $500)',
    LIMIT_HINT_TOKENS: 'Number of tokens (e.g. 1000000)',
    LIMIT_HINT_REQUESTS: 'Number of API requests (e.g. 1000)',
    SUBMIT_CREATE: 'Save Rule',
    SUBMIT_EDIT: 'Update Rule',
    SUBMIT_LOADING: 'Saving…',
    CANCEL: 'Cancel',
  },
  DIALOG: {
    CREATE_TITLE: 'Add Quota Rule',
    CREATE_DESCRIPTION:
      'Set a threshold for AI usage. The rule takes effect immediately and applies to the next period check.',
    EDIT_TITLE: 'Edit Quota Rule',
    EDIT_DESCRIPTION:
      'Update the quota rule. Changes take effect on the next quota check.',
    DELETE_TITLE: 'Delete Quota Rule',
    DELETE_DESCRIPTION:
      'This rule will no longer be applied. Affected users will be unblocked on the next quota check. This action cannot be undone.',
    DELETE_CONFIRM: 'Delete Rule',
    DELETE_CANCEL: 'Cancel',
    DELETE_LOADING: 'Deleting…',
  },
  EDIT_ARIA: 'Edit quota rule',
  DELETE_ARIA: 'Delete quota rule',
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load quota rules',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching AI cost quota rules.',
  },
} as const;

export const EVAL_DIMENSIONS_STRINGS = {
  NAV: { LABEL: 'Validation Configuration' },
  PAGE_TITLE: 'Validation Configuration',
  PAGE_DESCRIPTION:
    'Adjust evaluation dimension thresholds and weights without requiring a code deployment.',
  BREADCRUMB: 'Validation Configuration',
  TABLE: {
    DIMENSION_COLUMN: 'Dimension',
    DESCRIPTION_COLUMN: 'Description',
    PASSING_SCORE_COLUMN: 'Passing Score',
    WEIGHT_COLUMN: 'Weight',
    ACTIONS_COLUMN: '',
    NO_DATA: 'No evaluation dimensions found',
    NO_DATA_DESCRIPTION:
      'Evaluation dimensions will appear here once configured on the backend.',
    ERROR: 'Failed to load evaluation dimensions',
  },
  DIALOG: {
    EDIT_TITLE: 'Edit Dimension Thresholds',
    EDIT_DESCRIPTION:
      'Update the passing score and weight for this evaluation dimension. Changes apply to all subsequent evaluations.',
  },
  FORM: {
    PASSING_SCORE_LABEL: 'Passing Score',
    PASSING_SCORE_PLACEHOLDER: '1–5',
    PASSING_SCORE_HINT:
      'Note: Scores range from 1 to 5. Responses scoring below the configured threshold will be flagged for human review.',
    WEIGHT_LABEL: 'Weight',
    WEIGHT_PLACEHOLDER: '1–5',
    WEIGHT_HINT:
      "Note: Weightage range from 1 to 5. Higher weight increases a dimension's impact on the final score.",
    SUBMIT: 'Save Changes',
    CANCEL: 'Cancel',
  },
  EDIT_ARIA: 'Edit dimension thresholds',
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load evaluation dimensions',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching the evaluation dimension configuration.',
  },
} as const;

export const ADVERSARIAL_EVAL_STRINGS = {
  NAV: { LABEL: 'Adversarial Eval Agent' },
  PAGE_TITLE: 'Autonomous Adversarial Evaluation Agent',
  PAGE_DESCRIPTION:
    'Configure the autonomous agent that adversarially probes the assistant: the boundaries it must never cross, the policy it is graded against, and the results of each run.',
  BREADCRUMB: 'Autonomous Adversarial Evaluation Agent',
  TABS: {
    BOUNDARIES: 'Boundaries',
    POLICY: 'Policy',
  },
} as const;

export const AGENT_BOUNDARIES_STRINGS = {
  ADD_BUTTON: 'Add Boundary',
  TABLE: {
    NAME_COLUMN: 'Boundary',
    DESCRIPTION_COLUMN: 'Description',
    CREATED_COLUMN: 'Created',
    ACTIONS_COLUMN: '',
    STATUS_COLUMN: 'Status',
    STATUS_ACTIVE: 'Active',
    STATUS_INACTIVE: 'Inactive',
    NO_DATA: 'No agent boundaries defined',
    NO_DATA_DESCRIPTION:
      'Add a boundary to describe a rule the agent must never break.',
    ERROR: 'Failed to load agent boundaries',
    ERROR_DESCRIPTION: 'Refresh the page or try again in a moment.',
  },
  FORM: {
    NAME_LABEL: 'Name',
    NAME_PLACEHOLDER: 'e.g. No medical diagnosis',
    DESCRIPTION_LABEL: 'Description',
    DESCRIPTION_PLACEHOLDER:
      'Describe the boundary in full, e.g. the agent must never provide a medical diagnosis to the user',
    SUBMIT_CREATE: 'Save Boundary',
    SUBMIT_EDIT: 'Update Boundary',
    SUBMIT_LOADING: 'Saving…',
    CANCEL: 'Cancel',
  },
  DIALOG: {
    CREATE_TITLE: 'Add Agent Boundary',
    CREATE_DESCRIPTION:
      'Define a rule the adversarial evaluation agent will test the assistant against. It applies to every subsequent evaluation.',
    EDIT_TITLE: 'Edit Agent Boundary',
    EDIT_DESCRIPTION:
      'Update the boundary. Changes apply to every subsequent evaluation.',
  },
  EDIT_ARIA: 'Edit agent boundary',
  ACTIVATE_ARIA: 'Activate agent boundary',
  DEACTIVATE_ARIA: 'Deactivate agent boundary',
  TOAST: {
    CREATE_SUCCESS: 'Agent boundary created.',
    UPDATE_SUCCESS: 'Agent boundary updated.',
    ACTIVATE_SUCCESS: 'Agent boundary activated.',
    DEACTIVATE_SUCCESS: 'Agent boundary deactivated.',
    ERROR: 'Something went wrong. Please try again.',
  },
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load agent boundaries',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching the agent boundaries.',
  },
} as const;

export const EVALUATION_POLICY_STRINGS = {
  NEW_POLICY_BUTTON: 'New Policy',
  FORM: {
    NAME_LABEL: 'Policy Name',
    NAME_PLACEHOLDER: 'e.g. Companion safety policy v3',
    DESCRIPTION_LABEL: 'Policy Description',
    DESCRIPTION_PLACEHOLDER:
      'Describe what the assistant may do, the boundaries it must hold, when it must escalate, and the failure modes worth attacking.',
    DESCRIPTION_HINT:
      'Describe the application being tested. The rules it is graded against come from the Boundaries tab, not from this field.',
    ITERATIONS_LABEL: 'Number of Iterations',
    ITERATIONS_PLACEHOLDER: 'e.g. 10',
    // A function, not a fixed string, so the displayed limit can never drift
    // from EVALUATION_POLICY_LIMITS.ITERATIONS_MAX (the actual source of truth).
    ITERATIONS_HINT: (max: number) =>
      `How many adversarial attempts the agent makes (max ${max}). Each one spends model budget.`,
  },
  DIALOG: {
    TITLE: 'New Evaluation Policy',
    DESCRIPTION:
      'The agent derives what to attack and how to grade it from this policy. Saving starts a new evaluation run against it.',
    SUBMIT: 'Save & Run',
    SUBMIT_LOADING: 'Starting…',
    CANCEL: 'Cancel',
  },
  CHAR_COUNT: (used: number, max: number) => `${used}/${max}`,
  TOAST: {
    RUN_STARTED: 'Evaluation started.',
    RUN_ERROR: 'Could not start the evaluation. Please try again.',
  },
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load the evaluation policy',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching the evaluation policy.',
  },
} as const;

export const EVALUATION_RUNS_STRINGS = {
  TABLE: {
    STARTED_COLUMN: 'Created',
    NAME_COLUMN: 'Name',
    POLICY_COLUMN: 'Policy Description',
    USERNAME_COLUMN: 'Created By',
    STATUS_COLUMN: 'Status',
    ACTIONS_COLUMN: '',
    NO_DATA: 'No evaluation runs yet',
    NO_DATA_DESCRIPTION:
      'Use New Policy to save a policy and start your first evaluation.',
    ERROR: 'Failed to load evaluation runs',
    ERROR_DESCRIPTION: 'Refresh the page or try again in a moment.',
    EMPTY_VALUE: '—',
  },
  STATUS_LABELS: {
    PENDING: 'Pending',
    PROCESSING: 'Processing',
    PROCESSED: 'Complete',
    FAILED: 'Failed',
  } as const,
  DOWNLOAD_ARIA: 'Download run report as JSON',
  VIEW_POLICY_ARIA: 'View the full policy for this run',
  POLICY_DIALOG: {
    TITLE: 'Evaluation Policy',
    DESCRIPTION:
      'The policy this run was graded against, exactly as it was submitted.',
    READ_ONLY_NOTE:
      'Read-only. Use New Policy to submit a revised policy and start a new run.',
    STARTED_LABEL: 'Started',
    STARTED_BY_LABEL: 'Started by',
    NAME_LABEL: 'Policy name',
    ITERATIONS_LABEL: 'Iterations',
    DESCRIPTION_LABEL: 'Policy description',
    BOUNDARIES_LABEL: 'Boundaries evaluated',
    BOUNDARY_NOT_CONSIDERED: 'Not evaluated',
    RESULTS_LABEL: 'Results',
    RESULT_PASSED: 'Boundaries passed',
    RESULT_FAILED: 'Boundaries failed',
    RESULT_VULNERABILITIES: 'Vulnerabilities found',
    RESULT_ITERATIONS: 'Iterations run',
    PENDING_NOTE: 'This evaluation has not finished yet.',
    FAILED_LABEL: 'Why it failed',
    DOWNLOAD_UNAVAILABLE: 'Available once this evaluation finishes.',
  },
  TOAST: {
    DOWNLOAD_COMPLETE: 'Report downloaded.',
    DOWNLOAD_ERROR: 'Report download failed. Please try again.',
    DOWNLOAD_NOT_READY: 'This evaluation result is not ready yet.',
  },
} as const;

export const AUDIT_REPORTS_STRINGS = {
  NAV: { LABEL: 'Audit Reports' },
  PAGE_TITLE: 'Audit Reports',
  PAGE_DESCRIPTION:
    'Export lifecycle logs, audit records, and trace data as structured reports for governance and regulatory compliance.',
  BREADCRUMB: 'Audit Reports',
  FORM: {
    SECTION_TITLE: 'Generate Export',
    DATE_FROM_LABEL: 'Start Date',
    DATE_TO_LABEL: 'End Date',
    DATE_FROM_PLACEHOLDER: 'Select start date',
    DATE_TO_PLACEHOLDER: 'Select end date',
    REPORT_TYPE_LABEL: 'Report Type',
    REPORT_TYPE_PLACEHOLDER: 'Select report type',
    ENTITY_LABEL: 'Entity',
    ENTITY_PLACEHOLDER: 'Select entity',
    ENTITY_HINT: 'Required to generate the export',
    FORMAT_LABEL: 'Export Format',
    SUBMIT_BUTTON: 'Generate Export',
    SUBMIT_LOADING: 'Generating…',
  },
  REPORT_TYPE_LABELS: {
    ALL_QUERY_LIFECYCLES: 'All Query Lifecycles',
    ADMIN_ACTION_LOGS: 'Admin Action Logs',
  },
  ENTITY_LABELS: {
    USER: 'User',
    KNOWLEDGE_SOURCE: 'Knowledge Source',
    QUOTA_RULE: 'Quota Rule',
    EVALUATION_DIMENSION: 'Evaluation Dimension',
    DATA_RETENTION_POLICY: 'Data Retention Policy',
  },
  FORMAT_LABELS: {
    CSV: 'CSV',
    EXCEL: 'Excel (.xlsx)',
    JSON: 'JSON',
  },
  TOAST: {
    DOWNLOAD_COMPLETE: 'Report generated successfully.',
    DOWNLOAD_ERROR: 'Report generation failed. Please try again later.',
  },
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load audit reports',
    LOAD_FAILED_DESCRIPTION: 'An error occurred while loading this page.',
  },
} as const;

export const FORBIDDEN_STRINGS = {
  STATUS_CODE: '403',
  TITLE: 'Access Denied',
  DESCRIPTION: "You don't have permission to view this page.",
  ROLE_LABEL: 'Your role',
  CTA_BUTTON: 'Go to My Dashboard',
} as const;

export const QUOTA_STRINGS = {
  PREFIX_BY_PERIOD: {
    daily: "You've reached your daily limit.",
    weekly: "You've reached your weekly limit.",
    monthly: "You've reached your monthly limit.",
  },
  PREFIX_FALLBACK: "You've reached your usage limit.",
  FALLBACK: 'Come back later to continue.',
  RESET_TODAY: 'Your limit will reset today at',
  RESET_TOMORROW: 'Your limit will reset tomorrow at',
  RESET_ON: 'Your limit will reset on',
} as const;

export const DELETE_ACCOUNT_STRINGS = {
  SECTION_TITLE: 'Delete My Data',
  SECTION_DESCRIPTION:
    'Permanently erase your account and all associated personal data under GDPR Article 17 (Right to Erasure).',
  TRIGGER_BUTTON: 'Delete my account',
  DIALOG_TITLE: 'Permanently delete your account?',
  DIALOG_DESCRIPTION:
    'This action cannot be undone. The following data will be permanently erased from our systems:',
  DIALOG_DATA_LIST: [
    'Voice journal entries and transcripts',
    'Chat history and conversation transcripts',
    'Review records and submitted feedback',
    'Your account profile and all personal information',
  ],
  DIALOG_CONFIRM_BUTTON: 'Yes, delete everything',
  DIALOG_CONFIRMING_LABEL: 'Deleting…',
  DIALOG_CANCEL_BUTTON: 'Cancel',
  SUCCESS_TOAST: 'Your account and all data have been permanently deleted.',
  ERROR_TOAST: 'Failed to delete your account. Please try again.',
};
export const DISCLOSURE_STRINGS = {
  PAGE_TITLE: 'Human Review Disclosure',
  BACK_LABEL: 'Back',
  LAST_UPDATED_LABEL: 'Last updated',
  ERROR_TITLE: 'Failed to load disclosure',
  ERROR_MESSAGE: 'Unable to load the disclosure. Please try again.',
  RETRY_BUTTON: 'Retry',
  FOOTER_LINK_LABEL: 'Human Review Disclosure',
  LEARN_MORE_LABEL: 'Learn more about human review',
} as const;

export const DATA_RETENTION_STRINGS = {
  NAV: { LABEL: 'Data Retention' },
  PAGE_TITLE: 'Data Retention',
  PAGE_DESCRIPTION:
    'Configure how long each category of data is retained before it is automatically purged.',
  BREADCRUMB: 'Data Retention',
  TABLE: {
    CATEGORY_COLUMN: 'Category',
    EFFECTIVE_FROM_COLUMN: 'Effective From',
    RETENTION_COLUMN: 'Retention Period',
    STATUS_COLUMN: 'Status',
    ACTIONS_COLUMN: '',
    NO_DATA: 'No retention policies found',
    NO_DATA_DESCRIPTION:
      'Retention policies will appear here once configured on the backend.',
    ERROR: 'Failed to load data retention policies',
    STATUS_ACTIVE: 'Active',
    STATUS_DISABLED: 'Disabled',
  },
  DIALOG: {
    EDIT_TITLE: 'Edit Retention Policy',
    EDIT_DESCRIPTION:
      'Update the retention period, effective date, and enabled state for this data category.',
  },
  FORM: {
    RETENTION_LABEL: 'Retention Period (Days)',
    RETENTION_PLACEHOLDER: 'e.g. 30',
    RETENTION_HINT:
      'Enter the number of days to retain data (1–3650). Data older than this period will be automatically purged.',
    EFFECTIVE_FROM_LABEL: 'Effective From',
    EFFECTIVE_FROM_PLACEHOLDER: 'Select date',
    EFFECTIVE_FROM_HINT:
      'The date from which this retention policy becomes active.',
    ENABLED_LABEL: 'Policy Enabled',
    ENABLED_HINT:
      'When disabled, no automatic purge will run for this category.',
    SUBMIT: 'Save Changes',
    SUBMIT_LOADING: 'Saving…',
    CANCEL: 'Cancel',
    DAY_SINGULAR: 'day',
    DAY_PLURAL: 'days',
  },
  EDIT_ARIA: 'Edit retention policy',
  ERROR: {
    LOAD_FAILED_TITLE: 'Failed to load data retention policies',
    LOAD_FAILED_DESCRIPTION:
      'An error occurred while fetching the data retention policy configuration.',
  },
} as const;
